import type { TypedGraphQLQueryClient } from '@precon-tools-suite-by-shared/shared-graphql-client-js-generic';
import {
    GetDownloadableFilesDocument,
    type GetDownloadableFilesQuery,
    type DownloadableFileFragment,
} from './get-downloadable-file.generated';
import {
    FileDownloadError,
    FileNotFoundError,
    FileDownloadGraphQLError,
    NoFilesError,
    NodeNotFoundError,
    DownloadLinkMissingError,
} from './errors';
import type { ExecutionResult } from 'graphql';
import {
    isType,
    isA,
} from '@precon-tools-suite-by-shared/precon-graphql-types';
import { filenameFromId } from './fallback-filenames';

export interface DownloadableFile {
    id: string;
    filename: string;
    url: string;
}

export function prepareDownloadableFileFragment(
    file: DownloadableFileFragment | null | undefined,
    inputId = 'unknown'
): DownloadableFile {
    if (!file) throw FileNotFoundError.fromInputId(inputId);

    const url = file.url;
    if (!url) throw DownloadLinkMissingError.fromFragment(file);

    return {
        id: file.id,
        filename: file.filename ?? filenameFromId(file.id),
        url,
    };
}

export type DownloadableFilesNode = Exclude<
    GetDownloadableFilesQuery['node'],
    null | undefined
>;

export async function queryDownloadableFiles(
    client: TypedGraphQLQueryClient,
    id: string,
    first: number | null = null,
    after: string | null = null
): Promise<DownloadableFilesNode> {
    let result: ExecutionResult<GetDownloadableFilesQuery>;
    try {
        result = await client.query({
            query: GetDownloadableFilesDocument,
            variables: { id, first, after },
        });
    } catch (e) {
        throw FileDownloadGraphQLError.fromCaughtError(e);
    }

    if (result.errors && result.errors.length > 0)
        throw FileDownloadGraphQLError.fromGraphQLErrors(result.errors);

    if (!result.data || !result.data.node)
        throw NodeNotFoundError.fromNodeId(id);

    return result.data.node;
}

export function* extractDownloadableFileFragmentsFromResult(
    node: DownloadableFilesNode
) {
    if (isType(node, 'File')) {
        yield node;
    } else if (isA(node, 'HasFiles')) {
        const fileEdges = node.files?.edges ?? [];

        for (const fileEdge of fileEdges) {
            if (fileEdge && fileEdge.node) yield fileEdge.node;
        }
    }
}

export async function* downloadableFilesIterator(
    client: TypedGraphQLQueryClient,
    id: string,
    first: number | null = null,
    after: string | null = null
) {
    let hasNextPage: boolean;
    do {
        hasNextPage = false;
        try {
            const node = await queryDownloadableFiles(client, id, first, after);
            for (const file of extractDownloadableFileFragmentsFromResult(
                node
            )) {
                try {
                    yield prepareDownloadableFileFragment(file);
                } catch (e) {
                    if (e instanceof DownloadLinkMissingError) continue;
                    throw e;
                }
            }

            if (isA(node, 'HasFiles') && node.files?.pageInfo?.endCursor) {
                after = node.files.pageInfo.endCursor;
                hasNextPage = node.files.pageInfo.hasNextPage ?? false;
            }
        } catch (e) {
            if (e instanceof FileDownloadError) return;
            throw e;
        }
    } while (hasNextPage);
}

/**
 *
 * @parem client The GraphQL client to get the file metadata.
 * @param id The global-id of the file or the `HasFiles`-node for which you want to get the download info.
 */
export async function getDownloadableFile(
    client: TypedGraphQLQueryClient,
    id: string
): Promise<DownloadableFile> {
    const node = await queryDownloadableFiles(client, id, 1);

    const iteratorResult =
        extractDownloadableFileFragmentsFromResult(node).next();
    if (iteratorResult.done) throw NoFilesError.fromHasFilesNode(node.id);

    return prepareDownloadableFileFragment(iteratorResult.value, node.id);
}
